import React, { useState, useRef, useEffect } from 'react';
import { AVATAR_COLORS, initials, convName, fmtRel, notifPermission, inText, inTime } from './InboxAlerts';

const TABS = ['Todos', 'Mensajes'];

const channelLabel = (ch) => {
  if (ch === 'instagram') return 'Instagram';
  if (ch === 'messenger') return 'Messenger';
  return 'WhatsApp';
};

// items: chats con mensajes sin leer (vienen de useInboxAlerts en Layout, con la sesión del usuario)
export default function NotificationPanel({ items, onOpen, onClose, ignoreRef }) {
  const [activeTab, setActiveTab] = useState('Todos');
  const [readIds, setReadIds] = useState(new Set());
  const [perm, setPerm] = useState(notifPermission);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ignoreRef && ignoreRef.current && ignoreRef.current.contains(e.target)) return; // la campana abre/cierra
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, ignoreRef]);

  const notifs = (items || []).slice(0, 30).map(c => ({
    id: c.id,
    conv: c,
    type: 'message',
    unread: true,
    name: convName(c),
    channel: channelLabel(c.channel),
    time: fmtRel(inTime(c)),
    text: inText(c),
    count: c.unread_count || 1,
  }));
  const loading = items === null;

  const isUnread = (n) => n.unread && !readIds.has(n.id);
  const markAll = () => setReadIds(new Set(notifs.map(n => n.id)));
  const unreadCount = notifs.filter(n => isUnread(n)).length;

  const filtered = notifs.filter(n => {
    if (activeTab === 'Mensajes') return n.type === 'message';
    return true;
  });

  const askPerm = () => {
    try { Notification.requestPermission().then(p => setPerm(p)); } catch (e) {}
  };

  return (
    <>
      <style>{`
        .np-panel{position:fixed;top:58px;right:16px;width:min(380px,calc(100vw - 32px));background:#fff;border-radius:8px;box-shadow:0 12px 32px rgba(0,0,0,0.08),0 1px 3px rgba(0,0,0,0.04);border:1px solid #EFEFEF;z-index:2000;overflow:hidden;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;animation:np-in .18s cubic-bezier(.4,0,.2,1) both;}
        @keyframes np-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
        .np-header{display:flex;align-items:center;justify-content:space-between;padding:14px 16px 10px;}
        .np-title{font-size:15px;font-weight:500;color:#111;}
        .np-count{background:#F3F4F6;color:#374151;border:1px solid #EBEBEB;font-size:11px;font-weight:400;padding:1px 7px;border-radius:4px;}
        .np-mark-all{font-size:12px;color:#6B7280;background:none;border:none;cursor:pointer;font-family:inherit;padding:0;transition:color 0.15s;}
        .np-mark-all:hover{color:#111;}
        .np-tabs{display:flex;gap:4px;padding:0 16px 10px;}
        .np-tab{padding:4px 10px;border-radius:6px;font-size:12.5px;font-weight:400;cursor:pointer;transition:background .15s,color .15s,transform .15s;font-family:inherit;white-space:nowrap;line-height:1.4;}
        .np-tab:active{transform:scale(.96);}
        .np-tab.active{background:#F3F3F3;color:#111;border:1px solid #E5E7EB;}
        .np-tab:not(.active){background:transparent;color:#6B7280;border:1px solid transparent;}
        .np-tab:not(.active):hover{background:#F7F7F7;color:#111;}
        .np-divider{height:1px;background:#F3F3F3;}
        .np-list{max-height:450px;overflow-y:auto;}
        .np-list::-webkit-scrollbar{width:0;}
        .np-item{display:flex;gap:11px;padding:12px 16px;cursor:pointer;transition:background 0.15s;border-bottom:1px solid #F5F5F5;}
        .np-item:last-child{border-bottom:none;}
        .np-item:hover{background:#FAFAFA;}
        .np-item.unread{background:#fff;}
        .np-item.unread:hover{background:#FAFAFA;}
        .np-av-wrap{position:relative;flex-shrink:0;}
        .np-avatar{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12.5px;font-weight:500;color:#374151;}
        .np-dot{position:absolute;top:-1px;left:-1px;width:9px;height:9px;border-radius:50%;background:#22C55E;border:2px solid #fff;}
        .np-content{flex:1;min-width:0;}
        .np-item-top{font-size:13px;line-height:1.45;margin-bottom:2px;color:#111;}
        .np-item-name{font-weight:500;color:#111;}
        .np-item-action{color:#6B7280;}
        .np-badge{display:inline-flex;align-items:center;gap:3px;padding:1px 6px;background:#F7F7F7;border:1px solid #EBEBEB;border-radius:4px;font-size:11px;font-weight:400;color:#4B5563;vertical-align:middle;margin-left:2px;}
        .np-meta{font-size:11px;color:#9CA3AF;margin-bottom:7px;}
        .np-comment{padding:8px 10px;background:#F7F7F7;border-radius:6px;font-size:12.5px;color:#4B5563;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .np-unread-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;background:#111;color:#fff;font-size:10.5px;font-weight:400;border-radius:4px;margin-left:6px;vertical-align:middle;}
        .np-empty{padding:40px 20px;text-align:center;color:#9CA3AF;font-size:13px;}
        .np-loading{padding:40px 20px;text-align:center;}
        .np-spinner{width:20px;height:20px;border:2px solid #F0F0F0;border-top:2px solid #111;border-radius:50%;animation:np-spin 0.7s linear infinite;margin:0 auto;}
        .np-perm{display:flex;align-items:center;gap:10px;padding:10px 16px;background:#FAFAFA;border-bottom:1px solid #F3F3F3;font-size:12px;color:#4B5563;line-height:1.45;}
        .np-perm-btn{flex-shrink:0;height:28px;padding:0 12px;border-radius:6px;border:none;background:#111;color:#fff;font-family:inherit;font-size:12px;font-weight:400;cursor:pointer;transition:background .15s,transform .15s;}
        .np-perm-btn:hover{background:#2B2B2B;} .np-perm-btn:active{transform:scale(.96);}
        @keyframes np-spin{to{transform:rotate(360deg)}}
      `}</style>
      <div ref={ref} className="np-panel" onClick={e => e.stopPropagation()}>

        <div className="np-header">
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span className="np-title">Notificaciones</span>
            {unreadCount > 0 && <span className="np-count">{unreadCount}</span>}
          </div>
          {unreadCount > 0 && (
            <button className="np-mark-all" onClick={markAll}>Marcar todo leído</button>
          )}
        </div>

        <div className="np-tabs">
          {TABS.map(tab => (
            <button key={tab} className={`np-tab${activeTab===tab?' active':''}`} onClick={() => setActiveTab(tab)}>
              {tab}
            </button>
          ))}
        </div>
        <div className="np-divider"/>
        {perm === 'default' && (
          <div className="np-perm">
            <span>Activa las notificaciones de Windows para enterarte aunque Talku esté minimizado.</span>
            <button className="np-perm-btn" onClick={askPerm}>Activar</button>
          </div>
        )}
        {perm === 'denied' && (
          <div className="np-perm">
            <span>Las notificaciones están bloqueadas en este navegador. Actívalas en el candado junto a la dirección (talku.online) → Notificaciones → Permitir, y recarga.</span>
          </div>
        )}

        <div className="np-list">
          {loading ? (
            <div className="np-loading"><div className="np-spinner"/></div>
          ) : filtered.length === 0 ? (
            <div className="np-empty">Sin mensajes sin leer</div>
          ) : filtered.map(n => (
            <div key={n.id} className={`np-item${isUnread(n)?' unread':''}`} onClick={() => { onOpen(n.conv); onClose(); }}>
              <div className="np-av-wrap">
                <div className="np-avatar" style={{background:AVATAR_COLORS[(n.id||0)%AVATAR_COLORS.length]}}>{initials(n.name)}</div>
                {isUnread(n) && <div className="np-dot"/>}
              </div>
              <div className="np-content">
                <div className="np-item-top">
                  <span className="np-item-name">{n.name}</span>
                  <span className="np-item-action"> envió {n.count > 1 ? `${n.count} mensajes` : 'un mensaje'} en </span>
                  <span className="np-badge">{n.channel}</span>
                  {n.count > 1 && <span className="np-unread-badge">{n.count}</span>}
                </div>
                <div className="np-meta">{n.time} · Mensaje</div>
                {n.text && <div className="np-comment">{n.text}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
